import React, {
	createElement,
	CSSProperties,
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import {
	setPreviewState,
	switchEditState,
} from "../../functions/commentHelpers";
import { GlobalContext, UserContext } from "../../App";
import { UserConstants } from "../../constants";
import {
	obliterateAnnouncement,
	deleteAnnouncementReply,
	deleteLevelComment,
	deleteLevelCommentReply,
	deleteProfileComment,
	deleteProfileReply,
	obliterateAnnouncementReply,
	obliterateLevelComment,
	obliterateLevelCommentReply,
	obliterateProfileComment,
	obliterateProfileReply,
	editLevelCommentReply,
	editLevelComment,
	editProfileComment,
	editProfileCommentReply,
	editAnnouncementReply,
	editAnnouncement,
	toggleCommentPin,
	toggleCommentHighlight,
} from "../../api";

import { ThreadManagerProps } from "../ThreadManager/types";

type element =
	| "thread"
	| "copy"
	| "edit"
	| "pin"
	| "delete"
	| "obliterate"
	| "highlight"
	| "report";

interface props {
	elementsToDisplay: element[];
	comment: any;
	origin: "level" | "profile" | "announcement";
	type: "comment" | "reply";
	setComments: React.Dispatch<React.SetStateAction<any[]>>;
	elementProps?: {
		editContent?: string;
		parentId?: string;
		parentAuthor?: string;
	};
	thread: boolean;
}

interface buttonProps {
	id?: element;
	text?: number | undefined;
	show?: boolean;
	tooltip?: string;
	icon?: string | null;
	href?: string;
	onClick?: (e: React.MouseEvent<HTMLElement>) => any;
	disabled?: boolean;
	active?: boolean;
	activeColour?: string;
	editState: boolean;
}

const CommentButtons = ({
	elementsToDisplay,
	comment,
	origin,
	type,
	setComments,
	thread,
	elementProps = {},
}: props) => {
	const { currentUserData } = useContext<any>(UserContext);
	const { setThreadProps } = useContext<any>(GlobalContext);

	const [isQuerying, setIsQuerying] = useState<boolean>(false);
	const [clickedDelete, setClickedDelete] = useState<number>(0);
	const [clickedSave, setClickedSave] = useState<number>(0);

	const deleteIntervalRef = useRef<any>(null);
	const saveIntervalRef = useRef<any>(null);

	const userIsStaff: boolean = Boolean(!!currentUserData?.isStaff);
	const userIsAdmin: boolean = Boolean(!!currentUserData?.isAdmin);
	const authorIsAdmin: boolean = comment?.author?.mainRoles?.includes(
		UserConstants.MainRoles.ADMIN,
	);
	const authorId = comment?.author?._id || comment?.author || "";
	const isAuthor = Boolean(authorId === currentUserData?._id && authorId);
	const deleted = Boolean(
		comment?.visibility === "deleted" || comment?.deleted,
	);

	const modifiers = {
		modifiers: [
			{
				name: "offset",
				options: { offset: [0, 16] },
			},
		],
	};

	const initialThreadState: ThreadManagerProps = useMemo(
		() => ({
			document: null,
			setDocuments: null,
			origin: null,
		}),
		[],
	);

	useEffect(() => {
		if (clickedDelete === 0) {
			clearInterval(deleteIntervalRef.current);
		}
		if (clickedSave === 0) {
			clearInterval(saveIntervalRef.current);
		}
	}, [clickedDelete, clickedSave]);

	const editRequests = useMemo(
		() => ({
			level: {
				reply: editLevelCommentReply,
				comment: editLevelComment,
			},
			profile: {
				reply: editProfileCommentReply,
				comment: editProfileComment,
			},
			announcement: {
				reply: editAnnouncementReply,
				comment: editAnnouncement,
			},
		}),
		[],
	);

	const deleteRequests = useMemo(
		() => ({
			level: {
				delete: {
					reply: deleteLevelCommentReply,
					comment: deleteLevelComment,
				},
				obliterate: {
					reply: obliterateLevelCommentReply,
					comment: obliterateLevelComment,
				},
			},
			profile: {
				delete: {
					reply: deleteProfileReply,
					comment: deleteProfileComment,
				},
				obliterate: {
					reply: obliterateProfileReply,
					comment: obliterateProfileComment,
				},
			},
			announcement: {
				delete: {
					reply: deleteAnnouncementReply,
				},
				obliterate: {
					reply: obliterateAnnouncementReply,
					comment: obliterateAnnouncement,
				},
			},
		}),
		[],
	);

	const handleSave = useCallback(() => {
		if (elementProps?.editContent === comment?.content)
			return toast.error(
				`The true change was the difference we made in life, since there are none for this ${type}!`,
			);

		if (clickedSave === 0) {
			setClickedSave(5);
			saveIntervalRef.current = setInterval(
				() => setClickedSave((p) => p - 1),
				1000,
			);
			return toast.info(`Click again to confirm saving your changes.`); // exit early
		}

		setIsQuerying(true);
		const msg = toast.loading("Saving changes...");

		// @ts-ignore
		editRequests[origin][type](comment._id, {
			content: elementProps?.editContent,
		})
			.then(() => {
				// update the comment in question
				setComments((prevComments) =>
					prevComments.map((c) =>
						c._id === comment._id
							? {
									...c,
									content: elementProps?.editContent,
									edited: true,
									editState: false,
									previewState: false,
								}
							: c,
					),
				);
			})
			.finally(() => {
				setClickedSave(0);
				setIsQuerying(false);
				toast.dismiss(msg);
			});
	}, [
		editRequests,
		clickedSave,
		elementProps?.editContent,
		comment,
		setComments,
		origin,
		type,
	]);

	const handleDeletion = useCallback(
		(input) => {
			if (!clickedDelete) {
				setClickedDelete(5);
				deleteIntervalRef.current = setInterval(
					() => setClickedDelete((p) => p - 1),
					1000,
				);
				return toast.warn(
					`Are you sure you want to ${input} this ${type}? Click again to confirm`,
				); // exit early
			}

			setIsQuerying(true);
			const msg = toast.loading(`Processing...`);
			deleteRequests[origin][input][type](comment._id)
				.then(() => {
					setClickedDelete(0);
					if (thread) setThreadProps(initialThreadState);

					// set to deleted
					if (input === "delete")
						setComments((prev) =>
							prev.map((c) =>
								c._id === comment._id
									? {
											...c,
											deleted: true,
											visibility: "deleted",
										}
									: c,
							),
						);

					// remove entirely
					if (input === "obliterate") {
						setComments((prev) =>
							prev.filter((c) => c._id !== comment._id),
						);
					}
				})
				.finally(() => {
					setIsQuerying(false);
					toast.dismiss(msg);
				});
		},
		[
			clickedDelete,
			comment._id,
			origin,
			setComments,
			type,
			deleteRequests,
			initialThreadState,
			setThreadProps,
			thread,
		],
	);

	const openThread = useCallback(
		(e) => {
			e.preventDefault();
			const updateObj: ThreadManagerProps = {
				document: comment,
				setDocuments: setComments,
				origin: origin,
			};
			setThreadProps(updateObj);
		},
		[comment, origin, setComments, setThreadProps],
	);

	const commentOnlyRequests = useMemo(
		() => ({
			pin: toggleCommentPin,
			highlight: toggleCommentHighlight,
		}),
		[],
	);

	const makeBaseRequest: any = useCallback(
		(id, field, message) => {
			const msg = toast.loading(message);
			commentOnlyRequests?.[id]?.(comment?._id, origin)
				.then(() => {
					setComments((prev) =>
						prev.map((c) =>
							c._id === comment._id
								? {
										...c, // toggle the pin on the state
										[field]: !Boolean(c?.[field]),
									}
								: c,
						),
					);
				})
				.finally(() => toast.dismiss(msg));
		},
		[comment._id, commentOnlyRequests, setComments, origin],
	);

	const buttonsToRender: buttonProps[] = useMemo(() => {
		const baseUrl = window.location.origin + window.location.pathname;
		const replyCount =
			comment?.replyCount - (comment?.deleted_replies?.length ?? 0);
		const replyCount2 =
			comment?.replies?.length - (comment?.deleted_replies?.length ?? 0);

		const initialButtons = [
			{
				id: "thread" as element,
				tooltip: "Replies",
				icon: null,
				text: replyCount || replyCount2 || 0,
				editState: false,
			},
			{
				id: "thread" as element,
				tooltip: "Open thread",
				icon: "comment",
				onClick: openThread,
				href: `?referer=${comment?._id}`,
				editState: false,
			},

			{
				id: "copy" as element,
				tooltip: "Copy link",
				icon: "content_copy",
				onClick: (e) => {
					e.preventDefault();
					navigator.clipboard.writeText(
						`${baseUrl}${comment?._id ? `?referer=${elementProps?.parentId ?? comment?._id}` : ""}`,
					);
					toast.success("Copied link to thread!");
				},
				editState: false,
			},
			{
				id: "pin" as element,
				tooltip: comment?.pinned ? "Unpin comment" : "Pin comment",
				icon: "push_pin",
				active: comment?.pinned,
				activeColour: "rgba(83, 229, 10, 1)",
				show:
					userIsStaff ||
					elementProps?.parentAuthor === currentUserData?._id,
				onClick: (e) => {
					e.preventDefault();
					makeBaseRequest(
						"pin",
						"pinned",
						"Fumbling with the pins...",
					);
				},
				editState: false,
			},
			{
				id: "edit" as element,
				show:
					((userIsStaff && !authorIsAdmin) ||
						isAuthor ||
						userIsAdmin) &&
					!deleted,
				tooltip: `Edit ${type}`,
				icon: "edit",
				onClick: () => {
					switchEditState(comment._id, setComments);
					if (thread) {
						const doc = document.getElementById(
							`levelcomment-${comment._id}`,
						);
						if (doc) {
							const offset = 100;
							const y =
								doc.getBoundingClientRect().top +
								window.pageYOffset -
								offset;
							window.scrollTo({ top: y, behavior: "smooth" });
						}
						setThreadProps(initialThreadState);
					}
				},
				editState: false,
			},
			{
				id: "edit" as element,
				show: true,
				tooltip: comment?.previewState
					? "Turn off preview"
					: "Preview changes",
				icon: comment?.previewState ? "visibility_off" : "visibility",
				active: comment?.previewState,
				activeColour: "rgba(229, 225, 10, 1)",
				onClick: () => {
					setPreviewState(
						comment._id,
						setComments,
						!Boolean(comment?.previewState),
					);
				},
				editState: true,
			},
			{
				id: "edit" as element,
				show: true,
				tooltip: "Cancel editing",
				icon: "cancel",
				onClick: () => {
					switchEditState(comment._id, setComments);
					setPreviewState(comment._id, setComments, false);
				},
				editState: true,
			},
			{
				id: "edit" as element,
				show: true,
				tooltip: "Save edits",
				icon: "save",
				onClick: handleSave,
				disabled: clickedSave > 4,
				active: clickedSave > 0,
				activeColour: "rgba(13, 230, 42, 1)",
				editState: true,
			},
			{
				id: "highlight" as element,
				tooltip: comment?.highlighted ? "Remove review highlight" : "Highlight as review",
				icon: "reviews",
				active: comment?.highlighted,
				activeColour: "rgba(229, 203, 10, 1)",
				show:
					userIsStaff ||
					!!currentUserData?.subRoles?.includes(
						UserConstants.SubRoles.REVIEWER,
					),
				onClick: (e) => {
					e.preventDefault();
					makeBaseRequest(
						"highlight",
						"highlighted",
						"Toggling review highlight...",
					);
				},
				editState: false,
			},
			{
				id: "delete" as element,
				show: (userIsStaff || isAuthor) && !deleted,
				tooltip: `Delete ${type}`,
				icon: "delete",
				disabled: clickedDelete > 4,
				active: clickedDelete > 0,
				onClick: (e) => {
					e.preventDefault();
					handleDeletion("delete");
				},
				editState: false,
			},
			{
				id: "obliterate" as element,
				show: userIsAdmin && deleted,
				tooltip: `Obliterate ${type}`,
				icon: "local_fire_department",
				disabled: clickedDelete > 4,
				active: clickedDelete > 0,
				onClick: (e) => {
					e.preventDefault();
					handleDeletion("obliterate");
				},
				editState: false,
			},
		];

		// make sure conditions apply
		return initialButtons.filter((btn) => {
			return (
				elementsToDisplay.includes(btn.id as element) &&
				btn?.show !== false &&
				btn?.editState === !!comment?.editState
			);
		});
	}, [
		comment,
		elementsToDisplay,
		setComments,
		type,
		userIsAdmin,
		userIsStaff,
		authorIsAdmin,
		isAuthor,
		deleted,
		clickedDelete,
		handleDeletion,
		clickedSave,
		handleSave,
		openThread,
		initialThreadState,
		setThreadProps,
		thread,
		elementProps,
		currentUserData?._id,
		currentUserData?.subRoles,
		makeBaseRequest,
	]);

	return (
		<div
			className="thread-icon-container"
			style={{ position: "relative", right: "4px" }}>
			{buttonsToRender.map((btn, index) => {
				const disabled = !!btn?.disabled || isQuerying;
				const style: CSSProperties = {
					pointerEvents: disabled ? "none" : "auto",
					opacity: disabled ? 0.5 : 1,
					transition: "all 0.6s ease",
				};

				const innerStyle: CSSProperties = {
					...(btn?.active && {
						color: btn?.activeColour || "rgb(240, 60, 60)",
					}),
				};

				return (
					<Fragment key={comment?._id + "-" + btn.id + "-" + index}>
						<OverlayTrigger
							delay={{ show: 210, hide: 0 }}
							placement="top"
							overlay={
								<Tooltip id={btn.id}>
									<span>{btn?.tooltip}</span>
								</Tooltip>
							}
							popperConfig={modifiers}>
							<span className="align-middle">
								{createElement(
									btn.icon ? "a" : "span",
									{
										href: btn.href || "#!",
										onClick: disabled
											? (e) => e.preventDefault()
											: btn?.onClick,
										style,
									},
									btn.icon ? (
										<span
											style={innerStyle}
											className={`notranslate material-symbols-outlined thread-icon`}>
											{btn.icon}
										</span>
									) : (
										<span
											style={innerStyle}
											className={`thread-number`}>
											{btn.text}
										</span>
									),
								)}
							</span>
						</OverlayTrigger>
					</Fragment>
				);
			})}
		</div>
	);
};

export default CommentButtons;
